/**
 * Custom lines — the one place the SPA knows what a non-catalog retainer row is.
 *
 * `PRICING.md` §Custom lines, through `CustomLineSchema`: a fixed amount agreed with
 * one client for work the rate card does not sell as a block. It is **in the client's
 * billing currency** and is charged exactly as given — no bundle discount, no market
 * tier, no conversion, no rounding — and it is not a block for the discount on the
 * others. `RetainerItemView.kind` is what says which sort of row you are holding, and
 * `service` is `null` on a custom one.
 *
 * Six screens need some part of that fact and none of them may restate it:
 *
 * | Where | What it takes from here |
 * |---|---|
 * | `pages/client/billing/plan.ts` | `catalogItemsOf`, `customLinesOf` — what may be priced, and what rides along |
 * | `pages/client/billing/PackageSection.tsx` | `retainerItemKey`, `CustomItemRow` |
 * | `pages/client/requests/OpenDecisions.tsx` | `customLineChanges` — the before-and-after by name |
 * | `pages/studio/clientDetail/RetainerSection.tsx` | `retainerItemKey`, `CustomItemRow` |
 * | `pages/studio/clientDetail/EditRetainerDialog.tsx` | `catalogItemsOf`, `customLinesOf` |
 * | `components/studio/PackageBuilder.tsx`, `Breakdown.tsx` | `CustomLineList`, the words |
 *
 * It sits under `components/client/` for the reason `QuoteBuildUp.tsx` does: the
 * client's own billing page is the screen that owns the retainer's block table
 * (`mockups/client/billing.html` §1), and the studio's client detail renders the same
 * rows for a different reader. `components/studio/Breakdown.tsx` already imports that
 * neighbour, and `pages/client/widget/WidgetConfigForm.tsx` reaches the other way for
 * the same reason: the directory names who asked first, not who may ask.
 *
 * **`CustomItemRow` is one row for two tables.** `client/billing.html` §1 and
 * `studio/client-detail.html` §Retainer are both four columns — the thing, what it
 * includes, what it costs past that, and the amount — so a custom line reads the same
 * in both: its name and its scope, then "Custom line" and "fixed" where a block prints
 * an allowance and an overage price. Two copies of that row is how one of them ends up
 * printing a `null` allowance as an empty cell.
 */

import type {
  CustomLineInput,
  Money,
  MonthlyBreakdownView,
  RetainerItemView,
} from '@fareast/shared'

import { ScrollX, Stack } from '../portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { B, Muted, Num, Small, Xs } from '../portal/Text.js'

/* ---------------------------------------------------------------- the words */

/** What a custom row prints where a block prints its allowance. */
export const CUSTOM_LINE_LABEL = 'Custom line'

/** What it prints where a block prints its overage price. Nothing is metered. */
export const CUSTOM_LINE_FIXED = 'fixed — nothing is metered'

/** The build-up's subtotal of the blocks alone, before the custom lines are added. */
export const BLOCKS_SUBTOTAL_LABEL = 'Blocks, after rounding'

/** The build-up's subtotal of the custom lines. */
export const CUSTOM_TOTAL_LABEL = 'Custom lines'

/**
 * Why a reader is looking at an amount that no step above it arrives at.
 *
 * Every other figure in a package is the rate card's, worked out the same way twice.
 * A custom line is not, and a panel that listed one without saying so would be
 * inviting its reader to check arithmetic that was never done.
 */
export const CUSTOM_LINES_NOTE =
  'Custom lines are agreed per client and are not on the rate card. Each is charged exactly ' +
  'as agreed — the bundle discount, the market tier and the rounding to $10 apply to the ' +
  'blocks and never to these.'

/* ---------------------------------------------------------------- the facts */

/** A retainer row that is a block of a rate-card service. Its `service` is a code. */
export type CatalogItemView = RetainerItemView & {
  readonly kind: 'catalog'
  readonly service: string
}

/**
 * A block of the card, and the only kind of row anything may be priced from.
 *
 * `service !== null` is checked as well as the kind, because that is what the guard
 * promises the compiler — and `isCustomItem` is its exact complement, so a row is in
 * one list or the other and never in neither.
 */
export function isCatalogItem(item: RetainerItemView): item is CatalogItemView {
  return item.kind === 'catalog' && item.service !== null
}

/** A fixed line agreed with this client. Nothing about it is on the card. */
export function isCustomItem(item: RetainerItemView): boolean {
  return !isCatalogItem(item)
}

/** The blocks, in the retainer's own order. What `PackageItemInput` is built from. */
export function catalogItemsOf(items: readonly RetainerItemView[]): readonly CatalogItemView[] {
  return items.filter(isCatalogItem)
}

/** The custom lines, in the retainer's own order. */
export function customItemsOf(items: readonly RetainerItemView[]): readonly RetainerItemView[] {
  return items.filter(isCustomItem)
}

/**
 * The retainer's custom rows, back in the shape `CustomLineSchema` takes.
 *
 * `retainers_change_package`'s `custom_lines` is **the whole set after the change**,
 * so a proposal that leaves one out drops it. Every caller that proposes a change
 * therefore has to send the ones it is not touching, and this is where they come
 * from: `amount` on a custom row is already the agreed amount in the billing
 * currency, which is exactly what the input wants.
 */
export function customLinesOf(items: readonly RetainerItemView[]): readonly CustomLineInput[] {
  return customItemsOf(items).map((item) => ({
    name: item.service_name,
    scope: item.block_scope,
    amount: item.amount,
  }))
}

/**
 * A key for one retainer row.
 *
 * `item.service` was the key on both tables and it is `null` on a custom row, so two
 * of them collided into one React child. A custom line is "named once per package,
 * case-insensitively" (`CustomLineSchema`), which makes its name a key — prefixed, so
 * a line called `seo_geo` cannot collide with the block of that code.
 */
export function retainerItemKey(item: RetainerItemView): string {
  return isCatalogItem(item) ? item.service : `custom:${item.service_name}`
}

/** True when a build-up has custom lines to print between the blocks and the total. */
export function hasCustomLines(breakdown: MonthlyBreakdownView): boolean {
  return breakdown.custom_lines.length > 0
}

/* ----------------------------------------------------- one line, before and after */

/**
 * One custom line across a package change. `ComputedPrice.change.custom_lines`' shape.
 *
 * A computed price answers this list itself; a stored `RetainerChangeView` carries two
 * breakdowns instead, and `customLineChanges` derives the same thing from them so the
 * two screens print one table rather than two.
 */
export type CustomLineChange = {
  readonly name: string
  /** Its amount today, or null when the change adds it. */
  readonly amount_now: Money | null
  /** Its amount after, or null when the change drops it. */
  readonly amount_proposed: Money | null
}

/**
 * A custom line is named once per package, **case-insensitively** — so that is the key.
 *
 * `CustomLineSchema`'s own words, and the only way two lists of custom lines can be
 * matched up at all: they carry no code and no id, and a proposal is "the whole set
 * after the change", so "Service Schedule No. 1" re-priced and " service schedule
 * no. 1 " are the same line and not a drop plus an add. Exported because
 * `pages/client/billing/plan.ts` matches the same two lists for §1's sentence.
 */
export function customLineKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * The two lists of custom lines, matched by name: current ones first, then the new.
 *
 * The order is `ComputedPrice.change.custom_lines`' own — "current ones first, in
 * their order, then the new ones" — because the two renderings have to agree: a
 * client reading a proposal in Requests and a studio member reading the same proposal
 * in the builder are looking at one change.
 */
export function customLineChanges(
  before: MonthlyBreakdownView,
  after: MonthlyBreakdownView,
): readonly CustomLineChange[] {
  const proposed = new Map(after.custom_lines.map((line) => [customLineKey(line.name), line]))
  const held = new Set(before.custom_lines.map((line) => customLineKey(line.name)))

  return [
    ...before.custom_lines.map((line) => ({
      name: line.name,
      amount_now: line.amount,
      amount_proposed: proposed.get(customLineKey(line.name))?.amount ?? null,
    })),
    ...after.custom_lines
      .filter((line) => !held.has(customLineKey(line.name)))
      .map((line) => ({ name: line.name, amount_now: null, amount_proposed: line.amount })),
  ]
}

/* ------------------------------------------------------------------- the row */

/**
 * One custom line as a row of a four-column retainer table.
 *
 * The two tables it serves label their middle columns differently — "Blocks" and
 * "Allowance" on the studio's, "Included every month" and "Past the allowance" on the
 * client's — and a custom line's answer to both is the same two words either way,
 * which is why it is one component. Its scope is under its name because it is the
 * only description the line has: a block's scope is the card's and the card is a
 * click away, while this one was written for this client alone.
 */
export function CustomItemRow({ item }: { readonly item: RetainerItemView }) {
  return (
    <Tr>
      <Td>
        <B>{item.service_name}</B>
        {item.block_scope === '' ? null : (
          <Xs block>
            <Muted>{item.block_scope}</Muted>
          </Xs>
        )}
      </Td>
      <Td>
        <Small>
          <Muted>{CUSTOM_LINE_LABEL}</Muted>
        </Small>
      </Td>
      <Td>
        <Small>
          <Muted>{CUSTOM_LINE_FIXED}</Muted>
        </Small>
      </Td>
      <Td right num>
        <Num value={item.amount} />
      </Td>
    </Tr>
  )
}

/* --------------------------------------------------- the three build-up rows */

/**
 * The rows a build-up grows when the package has custom lines, and nothing when it
 * has none.
 *
 * `MonthlyBreakdownView` is rendered in three places — the studio's client detail,
 * the client's own billing page and `components/studio/Breakdown.tsx` — because each
 * interleaves it with a different table (that triplication predates this and is on
 * the report's list). What must **not** be written three times is this: `total =
 * blocks_total + custom_total`, every step above arrives at `blocks_total`, and the
 * rounding line now refers to the blocks alone. Get it wrong in one copy and that
 * page's column silently stops adding up.
 *
 * `labelSpan` is the only thing the three disagree about: two of the tables are four
 * columns with the amount last, the third is two.
 *
 * **Empty is nothing**, not three zero rows — `.docs/00-index.md` §6's rule for the
 * rounding line, for the same reason, and it is what keeps every existing page test
 * on a card-only package rendering exactly what it rendered before.
 */
export function CustomBreakdownRows({
  breakdown,
  labelSpan,
}: {
  readonly breakdown: MonthlyBreakdownView
  /** How many columns the label spans before the amount. The studio tables use 3. */
  readonly labelSpan: number
}) {
  if (!hasCustomLines(breakdown)) return null

  return (
    <>
      <Tr variant="sub">
        <Td colSpan={labelSpan}>{BLOCKS_SUBTOTAL_LABEL}</Td>
        <Td right num>
          <Num value={breakdown.blocks_total} />
        </Td>
      </Tr>
      {breakdown.custom_lines.map((line) => (
        <Tr key={`custom:${line.name}`}>
          <Td colSpan={labelSpan}>{line.name}</Td>
          <Td right num>
            <Num value={line.amount} />
          </Td>
        </Tr>
      ))}
      <Tr variant="sub">
        <Td colSpan={labelSpan}>{CUSTOM_TOTAL_LABEL}</Td>
        <Td right num>
          <Num value={breakdown.custom_total} />
        </Td>
      </Tr>
    </>
  )
}

/* ------------------------------------------------------------ the read-only list */

/**
 * The custom lines a package carries, listed and not editable.
 *
 * Both price composers show this: the studio's "Edit retainer" dialog, because a
 * proposal carries the lines it is not changing and a reader has to see what they are
 * about to re-send, and `PackageBuilder` itself, because the total under it includes
 * them and a build-up nobody can reconcile is the defect this component exists to
 * avoid. Empty is nothing at all, not an empty table.
 */
export function CustomLineList({ lines }: { readonly lines: readonly CustomLineInput[] }) {
  if (lines.length === 0) return null

  return (
    <Stack gap="tight">
      <ScrollX>
        <Table>
          <THead>
            <Tr>
              <Th scope="col">{CUSTOM_LINE_LABEL}</Th>
              <Th scope="col">What it covers</Th>
              <Th scope="col" right>
                Per month
              </Th>
            </Tr>
          </THead>
          <TBody>
            {lines.map((line) => (
              <Tr key={`custom:${line.name}`}>
                <Td>
                  <B>{line.name}</B>
                </Td>
                <Td>
                  <Small>
                    <Muted>{line.scope}</Muted>
                  </Small>
                </Td>
                <Td right num>
                  <Num value={line.amount} />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </ScrollX>
      <Small>
        <Muted>{CUSTOM_LINES_NOTE}</Muted>
      </Small>
    </Stack>
  )
}
